import { call, put, select, takeLatest } from 'redux-saga/effects';

import { CustomerUrl, Role } from '../../../../types';

import { fetchIntegrationsApi } from '../../integration/integration.action';

import { fetchCustomerApi, fetchCustomerFailureApi } from '../customer/customer.action';

import { getAccessToken, isSandbox } from '../../auth';

import storeService from '../../../../store';

import { decodeBase64Url } from '../../../../store/utils';

import {
  FETCH_PROFILE_API,
  fetchProfileFailureApi,
  fetchProfileSuccessApi,
  UPDATE_PROFILE_API,
  UPDATE_PROFILE_PICTURE_API,
  UPDATE_TEAM_SETTINGS_API,
  updateProfileFailureApi,
  updateProfilePicFailureApi,
  updateProfilePicSuccessApi,
  updateProfileSuccessApi,
  updateTeamSettingsFailureApi,
  updateTeamSettingsSuccessApi,
} from './profile.action';

import {
  ProfileCompanyAction,
  ProfileState,
  TeamSettings,
  UpdateTeamSettingsAction,
} from './profile.state';

import { default as ProfileService } from './profile.service';

import { RUM_CONTEXTS, updateViewContext } from 'services';

function* fetchProfileApiCall(action: ProfileCompanyAction): Generator<any> {
  try {
    const userProfile = (yield call(ProfileService.getProfile) as unknown) as ProfileState;
    const store = storeService.getStore();
    const token = getAccessToken(store.getState());
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    const role = (payload.sub?.role || payload.role) as Role;

    if (userProfile) {
      userProfile.role = role;
      updateViewContext(RUM_CONTEXTS.USER, {
        id: userProfile.uuid || userProfile.userUuid,
        email: userProfile.email,
      });

      updateViewContext(RUM_CONTEXTS.TEAM, {
        uuid: userProfile.teamId || userProfile.teamUuid,
        teamRole: role,
      });
    }

    if (action.payload) {
      const sandbox = (yield select(isSandbox)) as boolean;
      updateViewContext(RUM_CONTEXTS.SANDBOX, { sandbox: !!sandbox });
      if ([Role.SUPER_ADMINISTRATOR, Role.BILLING, Role.ADMINISTRATOR].includes(role)) {
        yield put(fetchCustomerApi(true));
      } else {
        yield put(fetchCustomerFailureApi({ error: 'Unauthorized' }));
      }
      if ([Role.SUPER_ADMINISTRATOR, Role.ADMINISTRATOR, Role.DEVELOPER].includes(role)) {
        yield put(fetchIntegrationsApi());
      }
    }
    yield put(fetchProfileSuccessApi(userProfile));
  } catch (error) {
    yield put(fetchProfileFailureApi(error));
  }
}

function* fetchProfileSaga(): Generator<any> {
  yield takeLatest(FETCH_PROFILE_API, fetchProfileApiCall);
}

function* updateProfileApiCall({ payload }: ProfileCompanyAction): Generator<any> {
  try {
    const response = (yield call(ProfileService.updateProfile, payload) as unknown) as ProfileState;
    yield put(updateProfileSuccessApi(response));
  } catch (error) {
    yield put(updateProfileFailureApi(error));
  }
}

function* updateProfileSaga(): Generator<any> {
  yield takeLatest(UPDATE_PROFILE_API, updateProfileApiCall);
}

function* updateProfilePicApiCall({ payload }: ProfileCompanyAction): Generator<any> {
  try {
    const response = (yield call(
      ProfileService.uploadProfilePic,
      payload,
    ) as unknown) as CustomerUrl;
    yield put(updateProfilePicSuccessApi(response));
  } catch (error) {
    yield put(updateProfilePicFailureApi(error));
  }
}

function* updateProfilePicSaga(): Generator<any> {
  yield takeLatest(UPDATE_PROFILE_PICTURE_API, updateProfilePicApiCall);
}

function* updateTeamSettingsApiCall({ payload }: UpdateTeamSettingsAction): Generator<any> {
  try {
    const response = (yield call(
      ProfileService.updateTeamSettings,
      payload,
    ) as unknown) as TeamSettings;
    yield put(updateTeamSettingsSuccessApi(response));
  } catch (error) {
    yield put(updateTeamSettingsFailureApi(error));
  }
}

function* updateTeamSettingsSaga(): Generator<any> {
  yield takeLatest(UPDATE_TEAM_SETTINGS_API, updateTeamSettingsApiCall);
}

export { fetchProfileApiCall, updateProfileApiCall, updateProfilePicApiCall };
export const profileSaga = [
  fetchProfileSaga(),
  updateProfileSaga(),
  updateProfilePicSaga(),
  updateTeamSettingsSaga(),
];
